import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";

const resolveList = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const normalizeDeliveryCenter = (center) => ({
  ...center,
  client: center?.client ?? null,
});

export const GetDeliveryCenters = createAsyncThunk(
  "deliveryCenters/getAll",
  async (arg = {}, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      // support both GetDeliveryCenters(token) and GetDeliveryCenters({ token, name, address, phone, whatsapp, email, page, per_page })
      let token = null;
      let params = {};

      if (typeof arg === 'string') {
        token = arg;
      } else if (typeof arg === 'object' && arg !== null) {
        token = arg?.token ?? null;
        params = { ...arg };
        if (params.token) delete params.token;
      }

      // remove empty params (null, undefined, empty string) to avoid sending client_id=null
      Object.keys(params).forEach((k) => {
        if (params[k] === null || params[k] === undefined || params[k] === '') {
          delete params[k];
        }
      });

      const query = new URLSearchParams(params).toString();
      const url = `${config.apiUrl}/delivery-centers${query ? `?${query}` : ''}`;

      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // DEBUG: log URL and params to help reproduce client_id filter issue
      // (temporary - will remove after debugging)
      // eslint-disable-next-line no-console
      console.debug('[GetDeliveryCenters] GET', url, 'params=', params);

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({
        message: error?.message ?? "Unable to fetch delivery centers",
      });
    }
  }
);

export const AddDeliveryCenter = createAsyncThunk(
  "deliveryCenters/create",
  async ({ token, values }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const response = await fetch(`${config.apiUrl}/delivery-centers`, {
        method: "POST",
        body: JSON.stringify(values),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({
        message: error?.message ?? "Unable to create delivery center",
      });
    }
  }
);

export const UpdateDeliveryCenter = createAsyncThunk(
  "deliveryCenters/update",
  async ({ token, id, values }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const response = await fetch(`${config.apiUrl}/delivery-centers/${id}`, {
        method: "PUT",
        body: JSON.stringify(values),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({
        message: error?.message ?? "Unable to update delivery center",
      });
    }
  }
);

export const DeleteDeliveryCenter = createAsyncThunk(
  "deliveryCenters/delete",
  async ({ token, id }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const response = await fetch(`${config.apiUrl}/delivery-centers/${id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.status === 204 ? {} : await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return fulfillWithValue({
        id,
        message: data?.message ?? null,
      });
    } catch (error) {
      return rejectWithValue({
        message: error?.message ?? "Unable to delete delivery center",
      });
    }
  }
);

const initialState = {
  deliveryCenters: [],
  loading: false,
  error: null,
  successMessage: null,
  selectedDeliveryCenter: null,
  // pagination
  total: 0,
  page: 1,
  per_page: 10,
};

const DeliveryCentersSlice = createSlice({
  name: "deliveryCenters",
  initialState,
  reducers: {
    selectDeliveryCenter: (state, action) => {
      state.selectedDeliveryCenter = action.payload;
    },
    clearDeliveryCenterFeedback: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetDeliveryCenters.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(GetDeliveryCenters.fulfilled, (state, action) => {
        state.loading = false;
        const list = resolveList(action.payload).map(normalizeDeliveryCenter);
        state.deliveryCenters = list;
        state.error = null;
        state.successMessage = null;

        // pagination meta if returned
        if (Array.isArray(action.payload?.data?.data)) {
          state.total = action.payload.data.total ?? state.total;
          state.per_page = action.payload.data.per_page ?? state.per_page;
          state.page = action.payload.data.current_page ?? state.page;
        } else if (Array.isArray(action.payload?.data)) {
          state.total = action.payload.data.length;
          state.page = 1;
        } else if (Array.isArray(action.payload)) {
          state.total = action.payload.length;
        }
      })
      .addCase(GetDeliveryCenters.rejected, (state, action) => {
        state.loading = false;
        state.deliveryCenters = [];
        state.error = formatApiError(action.payload, "Unable to fetch delivery centers");
        state.successMessage = null;
      })
      .addCase(AddDeliveryCenter.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(AddDeliveryCenter.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message ?? null;
        state.error = null;
        const created = action.payload?.data;
        if (created) {
          state.deliveryCenters = [...state.deliveryCenters, normalizeDeliveryCenter(created)];
        }
      })
      .addCase(AddDeliveryCenter.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, "Unable to create delivery center");
        state.successMessage = null;
      })
      .addCase(UpdateDeliveryCenter.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(UpdateDeliveryCenter.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message ?? null;
        state.error = null;
        const updated = action.payload?.data ? normalizeDeliveryCenter(action.payload.data) : null;
        if (updated) {
          const index = state.deliveryCenters.findIndex((center) => center.id === updated.id);
          if (index !== -1) {
            state.deliveryCenters[index] = {
              ...state.deliveryCenters[index],
              ...updated,
            };
          }
        }
      })
      .addCase(UpdateDeliveryCenter.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, "Unable to update delivery center");
        state.successMessage = null;
      })
      .addCase(DeleteDeliveryCenter.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(DeleteDeliveryCenter.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message ?? null;
        state.error = null;
        const id = action.payload?.id;
        if (id) {
          state.deliveryCenters = state.deliveryCenters.filter((center) => center.id !== id);
        }
      })
      .addCase(DeleteDeliveryCenter.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, "Unable to delete delivery center");
        state.successMessage = null;
      });
  },
});

export default DeliveryCentersSlice.reducer;
export const { selectDeliveryCenter, clearDeliveryCenterFeedback } = DeliveryCentersSlice.actions;
